/**
接入腾讯IM
https://cloud.tencent.com/document/product/269/75295
**/
import TencentCloudChat from '@tencentcloud/chat/index.es.js';
import GroupModule from '@tencentcloud/chat/modules/group-module.js';
import EventEmitter from 'event-emitter';
import to from 'await-to-js';
import log from 'loglevel';
import EVENT from '../init/event_enum.js';
import { formatTime } from '../utils/utils';

class Tim {
  constructor(options, msgIns) {
    this._msgIns = msgIns; // 消息sdk interfaces 实例
    this.sdk_app_id = options.sdk_app_id;
    this.user_id = options.user_id;
    this.user_sig = options.user_sig;
    this.group_list = options.group_list || []; // 分组列表
    this.chatIns = null; // im实例
    this.createTime = Date.now();
    this.retryCount = 0;
    this.isLogining = false; // 记录登录状态，防止重复请求登录
    EventEmitter(this);
  }

  // 腾讯IM链接初始化
  async initialize() {
    this.retryCount++;

    if (!this.chatIns) {
      this.chatIns = this._create();
    }
    if (!this.chatIns) return;

    if (this.chatIns.isReady()) {
      // 已经登录
      await this.joinGroups();
    } else {
      if (!this.isLogining) {
        // 没有登录先登录
        await this._login();

        // 再加入群组
        await this.joinGroups();
      }
    }
  }

  // 创建 SDK 实例
  _create() {
    if (this.chatIns) return this.chatIns;
    try {
      // 创建 SDK 实例，`TencentCloudChat.create()`方法对于同一个 `SDKAppID` 只会返回同一份实例
      this.chatIns = TencentCloudChat.create({
        SDKAppID: this.sdk_app_id,
        modules: {
          'group-module': GroupModule // 群和群成员
        },
        unlimitedAVChatRoom: true
      });
      this.chatIns.setLogLevel(process.env.TIM_LOGLEVEL); // 日志
      // 监听事件
      this.initEvents();

      log.info('[tim] 创建 TencentCloudChat 实例成功');
      return this.chatIns;
    } catch (ex) {
      log.error('[tim] 创建 TencentCloudChat 实例失败：', ex);
      this._onCreateFailed(ex);
      return null;
    }
  }

  // 登录
  async _login() {
    this.isLogining = true;
    const params = {
      userID: this.user_id,
      userSig: this.user_sig
    };
    const [err, res] = await to(this.chatIns.login(params));
    this.isLogining = false;
    // 7501 登录正在执行中，例如，第一次 login 或 autoLogin 操作在回调前，后续的 login 或 autoLogin 操作会返回该错误码。
    // 2025 重复登录
    let isLogined = res?.code === 0 || res?.code === 7501 || err?.code === 2025;
    const result = err || res;
    this._onLoginStatus({ isLogined, ...result });

    return isLogined;
  }

  // 加入群组
  async joinGroups() {
    let promiseAllGroup = [];
    for (const group of this.group_list) {
      let itemPromise = this.chatIns.joinGroup({
        groupID: group.group_id,
        type: group.group_type
      });
      promiseAllGroup.push(itemPromise);
    }
    const joinResult = await Promise.allSettled(promiseAllGroup);
    const successGroupIds = [];
    for (let i = 0; i < joinResult.length; i++) {
      const rlt = joinResult[i];
      // log.error('[tim] 创建 加入群组结果：', rlt);
      // 10013(全量sdk返回)、2903(按需引入GroupModule返回) 表示重复加入
      if (rlt?.reason?.code === 10013 || rlt?.reason?.code === 2903 || rlt?.value?.code === 0) {
        // 加入成功的组
        successGroupIds.push(this.group_list[i].group_id);
      } else {
        // log.error('[tim] 创建 加入群组失败：', rlt);
      }
    }
    // console.log('======successGroupIds:', successGroupIds);
    // 数量相等说明都加入成功了
    const isJoinAllSuccess = successGroupIds.length === this.group_list.length;
    this._onJoinGroups(isJoinAllSuccess);

    if (!isJoinAllSuccess) {
      // 有加入失败的情况，其它已经加入成功的组，做一个退出处理
      successGroupIds.forEach(groupID => {
        this.chatIns.quitGroup(groupID);
      });
    }
  }

  // 退出全部群组，不考虑成功失败的情况
  async quitGroups() {
    if (!this.chatIns) return;
    const promisesArr = [];
    for (const group of this.group_list) {
      let itemPromise = this.chatIns.quitGroup(group.group_id);
      promisesArr.push(itemPromise);
    }
    await Promise.allSettled(promisesArr);
  }

  // 退出登录。 调用此接口的实例会发布 SDK_NOT_READY 事件，此时该实例下线，无法收、发消息。
  async logout() {
    if (!this.chatIns) return;
    const [err, res] = await to(this.chatIns.logout());
    // 2024 用户未登录
    if (err?.code == 2024 || res?.code == 0) {
      console.log('退出成功');
    }
  }

  getLoginStatus() {
    return this.chatIns?.isReady();
  }

  // disconnect
  async disconnect() {
    if (!this.chatIns) {
      log.warn('tim chat instance is null when call the disconnect method');
      return;
    }
    // 移除事件
    this.removeEvents();

    // 销毁 SDK 实例。SDK 会先 logout，然后断开 WebSocket 长连接，并释放资源。
    this.chatIns.destroy();
    this.chatIns = null;
  }

  // 销毁
  async destroy() {
    if (!this.chatIns) {
      log.warn('tim chat instance is null when call the destroy method');
      return;
    }
    this.chatIns.destroy();
    this.chatIns = null;
  }

  // 初始化事件
  initEvents() {
    this.chatIns.on(TencentCloudChat.EVENT.MESSAGE_RECEIVED, this._onTimMessageReceived.bind(this));
    this.chatIns.on(TencentCloudChat.EVENT.KICKED_OUT, this._onTimKickedOut.bind(this));
    this.chatIns.on(TencentCloudChat.EVENT.NET_STATE_CHANGE, this._onTimNetStateChange.bind(this));
  }

  removeEvents() {
    if (this.chatIns) {
      this.chatIns.off(TencentCloudChat.EVENT.MESSAGE_RECEIVED, this._onTimMessageReceived.bind(this));
      this.chatIns.off(TencentCloudChat.EVENT.KICKED_OUT, this._onTimKickedOut.bind(this));
      this.chatIns.off(TencentCloudChat.EVENT.NET_STATE_CHANGE, this._onTimNetStateChange.bind(this));
    }
  }

  // [自定义] 实例创建是你
  _onCreateFailed(error) {
    console.error(error);
    this.emit('onTimCreateFailed'); // 发送到adapter中处理
  }

  // [自定义] 登录结果监听处理
  _onLoginStatus(result) {
    if (!result.isLogined) {
      log.error('[tim]  登录失败 login error:', result); // 登录失败的相关信息
      // result.code  result.message
    } else {
      log.info('[tim] 登录成功');
    }
  }

  // [自定义] 加入全部群组的结果监听处理
  _onJoinGroups(status) {
    if (status) {
      log.info('[tim] 加入全部群组成功');
      this.emit(EVENT.TIM_JOIN_GROUP_SUCCESS); // 发送到adapter中处理
      this._msgIns.emit(EVENT.TIM_JOIN_GROUP_SUCCESS);
    } else {
      log.error('[tim] 加入全部群组失败');
      this.emit(EVENT.TIM_JOIN_GROUP_FAILED); // 发送到adapter中处理
      this._msgIns.emit(EVENT.TIM_JOIN_GROUP_FAILED);
    }
  }

  // [TIM] SDK 收到推送的单聊、群聊、群提示、群系统通知的新消息，接入侧可通过遍历 event.data 获取消息列表数据并渲染到页面
  _onTimMessageReceived(event) {
    // 可接收到单聊、群聊、群提示、群系统通知的新消息 https://cloud.tencent.com/document/product/269/75319
    // event.data - 存储 Message 对象的数组 - [Message]
    log.info(`[tim] ${formatTime(new Date())} 触发 onMessageReceived 事件`, event);
    const messageList = event.data;
    messageList.forEach(message => {
      if (message.type === TencentCloudChat.TYPES.MSG_TEXT) {
        // 文本消息
        // message.payload.text
        // 是该群组的消息才接收
        if (this.group_list.findIndex(g => g.group_id === message.to) > -1) {
          this.emit('onMessageReceived', message.payload.text); // 发送到adapter中处理
        }
      } else if (message.type === TencentCloudChat.TYPES.MSG_CUSTOM) {
        // 自定义消息
        // message.payload.data 自定义消息的 data 字段
        // message.payload.description 自定义消息的 description 字段
        // message.payload.extension 自定义消息的 extension 字段
        // 是该群组的消息才接收
        if (this.group_list.findIndex(g => g.group_id === message.to) > -1) {
          this.emit('onMessageReceived', message.payload.data); // 发送到adapter中处理
        }
      }
    });
  }

  // [TIM]踢出事件
  _onTimKickedOut(event) {
    // console.log(event.data.type);
    // TencentCloudChat.TYPES.KICKED_OUT_MULT_ACCOUNT(Web端，同一账号，多页面登录被踢)
    // TencentCloudChat.TYPES.KICKED_OUT_MULT_DEVICE(同一账号，多端登录被踢)
    // TencentCloudChat.TYPES.KICKED_OUT_USERSIG_EXPIRED(签名过期)
    // TencentCloudChat.TYPES.KICKED_OUT_REST_API(REST API kick 接口踢出)
    log.info('[tim] 触发 onKickedOut 事件', event.data.type);
    this._msgIns.emit(EVENT.TIM_KICKED_OUT, event.data.type);
  }

  // [TIM]网络变化事件
  _onTimNetStateChange(event) {
    // event.data.state 当前网络状态，枚举值及说明如下：
    // TencentCloudChat.TYPES.NET_STATE_CONNECTED - 已接入网络
    // TencentCloudChat.TYPES.NET_STATE_CONNECTING
    // 连接中。很可能遇到网络抖动，SDK 在重试。接入侧可根据此状态提示“当前网络不稳定”或“连接中”
    // TencentCloudChat.TYPES.NET_STATE_DISCONNECTED
    // 未接入网络。接入侧可根据此状态提示“当前网络不可用”。SDK 仍会继续重试，若用户网络恢复，SDK 会自动同步消息
    log.info('[tim] 触发 onNetStateChange 事件', event.data.state);
    this._msgIns.emit(EVENT.TIM_NET_STATE_CHANGE, event.data.state);
  }
}

export default Tim;
